/**
 * Kostenübernahme mit Versionen (Konzept KÜ V2, Stufe S1, Antoine 28.09.2026).
 *
 * V1 bleibt als Nachweis sichtbar und herunterladbar, auch wenn V2 schon unterwegs oder
 * unterschrieben ist. Welche Version gilt, entscheidet der Server (`gilt`, aus
 * `public.gueltige_kostenuebernahme`) — hier wird nur dargestellt, nie selbst gerechnet.
 *
 * Erscheint im Kostenübernahme-Drawer der Vorgangsseite und auf der Abrechnungsseite (beide rendern
 * `KostenuebernahmeDetail`, das diese Liste unten anhängt), sobald es mehr als eine Version gibt.
 */
import { useState } from "react";
import { apiMutate, useApi } from "../../hooks/useApi";
import { AENDERUNGS_ANLASS_TEXT } from "../../lib/activity-labels";
import { openAuthenticatedFile } from "../../lib/files";
import { currentMembership } from "../../lib/membership";
import { Button, Icon, StatusPill, useBestaetigung } from "../components";
import { formatDate, formatDateTime, money, type StatusMeta } from "../format";
import type { Me } from "../types";

/** Antwortform von `GET /api/events/:id/cost-acceptances`. */
export interface KueVersion {
  id: string;
  status: "sent" | "signed" | "declined" | "withdrawn" | "draft" | "signed_ungueltig";
  isTest: boolean;
  versionNo: number | null;
  supersedesId: string | null;
  supersededById: string | null;
  supersededAt: string | null;
  aenderungsAnlass: "gastwunsch" | "betriebsfehler" | "sonstiges" | null;
  offerVersion: number | null;
  signUrl: string | null;
  signedPdfUrl: string | null;
  signedPdfSha256: string | null;
  signerName: string | null;
  amountGrossCents: number | null;
  sentAt: string | null;
  signedAt: string | null;
  gilt: boolean;
}

export const ANLASS_TEXT: Record<NonNullable<KueVersion["aenderungsAnlass"]>, string> = AENDERUNGS_ANLASS_TEXT;

export const versionsName = (v: Pick<KueVersion, "versionNo" | "isTest">): string => (v.isTest ? "Test" : `V${v.versionNo ?? "?"}`);

/** „KÜ V1 vom 12.09.2026 über 990,00 €“ — die gültige Version in einem Satzteil. */
export const gueltigeText = (v: KueVersion): string =>
  `KÜ ${versionsName(v)} vom ${formatDate(v.signedAt)} über ${v.amountGrossCents != null ? money(v.amountGrossCents) : "den Betrag gemäß Bestellung"}`;

/** Status einer Version, wie der Betreiber ihn lesen soll. */
export function versionsStand(v: KueVersion, alle: KueVersion[]): StatusMeta {
  if (v.status === "signed") {
    if (v.gilt) return { label: "unterschrieben · gilt", tone: "success" };
    const neu = alle.find((x) => x.id === v.supersededById);
    if (neu) return { label: `ersetzt durch ${versionsName(neu)} am ${formatDate(v.supersededAt)}`, tone: "neutral" };
    return { label: "unterschrieben", tone: "neutral" };
  }
  if (v.status === "sent") return { label: "wartet auf Unterschrift", tone: "warn" };
  if (v.status === "declined") return { label: "abgelehnt", tone: "neutral" };
  // sql/770: unterschrieben, waehrend das Angebot nach dem Versand geaendert war - Nachweis, nicht verbindlich.
  if (v.status === "signed_ungueltig") return { label: "unterschrieben, ungültig – Angebot wurde geändert", tone: "warn" };
  return { label: "zurückgezogen", tone: "neutral" };
}

export function useKueVersionen(eventId: string | null) {
  return useApi<KueVersion[]>(eventId ? `/api/events/${eventId}/cost-acceptances` : null);
}

export function KostenuebernahmeVersionen({ eventId, onVerworfen }: { eventId: string; onVerworfen?: () => void }) {
  const q = useKueVersionen(eventId);
  const alle = q.data ?? [];
  const [kopiert, setKopiert] = useState(false);
  if (alle.length < 2) return null;
  const hatGueltige = alle.some((v) => v.gilt);

  return (
    <div className="mt-3 border-t border-outline-variant/30 pt-3 dark:border-outline/30" data-testid="kue-versionen">
      <p className="font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">Versionen</p>
      <ul className="mt-1.5 space-y-2">
        {alle.map((v) => {
          const stand = versionsStand(v, alle);
          const zeile =
            v.status === "signed"
              ? [`Unterschrieben ${formatDateTime(v.signedAt)}`, v.signerName ? `von ${v.signerName}` : null]
              : [v.sentAt ? `Zugestellt ${formatDateTime(v.sentAt)}` : null];
          const meta = [
            ...zeile,
            v.offerVersion != null ? `Angebotsfassung ${v.offerVersion}` : null,
            v.aenderungsAnlass ? `Anlass: ${ANLASS_TEXT[v.aenderungsAnlass]}` : null,
          ].filter(Boolean);
          return (
            <li key={v.id} className="rounded-lg bg-surface-container-low px-3 py-2 dark:bg-surface-variant/10" data-testid={`kue-version-${versionsName(v)}`}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{versionsName(v)}</span>
                <StatusPill meta={stand} />
                <span className="ml-auto font-body-md text-body-md tabular-nums text-on-background dark:text-inverse-on-surface">
                  {v.amountGrossCents != null ? money(v.amountGrossCents) : "A-la-carte"}
                </span>
              </div>
              {meta.length > 0 && <p className="mt-0.5 break-words font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">{meta.join(" · ")}</p>}
              {v.status === "signed" && v.signedPdfSha256 ? (
                <button
                  type="button"
                  onClick={() => void openAuthenticatedFile(`/api/events/${eventId}/cost-acceptances/${v.id}/pdf`)}
                  className="mt-1 inline-flex items-center gap-1.5 font-label-md text-[12px] text-primary hover:underline"
                >
                  <Icon name="picture_as_pdf" className="text-[15px]" /> PDF {versionsName(v)}
                </button>
              ) : v.status === "signed" && v.signedPdfUrl ? (
                <a href={v.signedPdfUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 font-label-md text-[12px] text-primary hover:underline">
                  <Icon name="picture_as_pdf" className="text-[15px]" /> PDF {versionsName(v)}
                </a>
              ) : null}
              {/* KÜ V2 (S2): die offene neue Fassung — Link weitergeben oder die Änderung verwerfen (V1 gilt weiter). */}
              {v.status === "sent" && !v.isTest && (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-primary" data-testid={`kue-version-aktionen-${versionsName(v)}`}>
                  {v.signUrl && (
                    <button
                      type="button"
                      onClick={() => void navigator.clipboard?.writeText(v.signUrl!).then(() => setKopiert(true)).catch(() => undefined)}
                      className="inline-flex items-center gap-1.5 font-label-md text-[12px] hover:underline"
                    >
                      <Icon name="content_copy" className="text-[15px]" /> {kopiert ? "Link kopiert" : "Signaturlink kopieren"}
                    </button>
                  )}
                  {hatGueltige && <AenderungVerwerfenKnopf eventId={eventId} versionen={alle} onVerworfen={onVerworfen} />}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * KÜ V2, Stufe S2 (E3): „ÄNDERUNG VERWERFEN“ — im Banner und im Kostenübernahme-Drawer.
 *
 * Der Gast unterschreibt die geänderte Fassung nicht, oder die Änderung soll doch nicht kommen:
 * dann gilt V1 weiter, und der Vorgang kehrt dorthin zurück. Die offene neue Version wird beim
 * Anbieter widerrufen (ihr Link wird ungültig), an V1 ändert sich nichts, an den Gast geht keine
 * Mail. Nur Inhaber — der Server lehnt sonst ab und die Meldung steht hier.
 */
export function AenderungVerwerfenKnopf({
  eventId,
  versionen,
  onVerworfen,
  variante = "link",
}: {
  eventId: string;
  versionen: KueVersion[];
  onVerworfen?: () => void;
  /** `knopf`: ein richtiger Knopf (Statuskarte im Builder); `link`: der unterstrichene Textlink (Drawer). */
  variante?: "link" | "knopf";
}) {
  const { bestaetigen, dialog } = useBestaetigung();
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Zweitprüfung H7: nur Owner/Admin sehen den Knopf (der Server lehnt sonst ohnehin mit 403 ab).
  const me = useApi<Me>("/api/me").data;
  const rolle = Array.isArray(me?.memberships) ? currentMembership(me)?.role : undefined;
  const gueltige = versionen.find((v) => v.gilt) ?? null;
  if (!gueltige || (rolle !== "owner")) return null;
  const offen = versionen.find((v) => v.status === "sent" && !v.isTest) ?? null;

  async function verwerfen() {
    if (!gueltige) return;
    const ok = await bestaetigen({
      titel: "Änderung verwerfen?",
      text: (
        <>
          {offen ? `Die offene Kostenübernahme ${versionsName(offen)} wird beim Anbieter widerrufen, ihr Link wird ungültig. ` : ""}
          Der Vorgang kehrt zur {gueltigeText(gueltige)}
          {gueltige.offerVersion != null ? ` (Angebotsfassung ${gueltige.offerVersion})` : ""} zurück. An den Gast geht keine Mail.
        </>
      ),
      bestaetigen: "Änderung verwerfen",
      gefahr: true,
    });
    if (!ok) return;
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/amendment/discard`, "POST", {});
      if (onVerworfen) onVerworfen();
      else window.location.reload();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {variante === "knopf" ? (
        <Button variant="secondary" data-testid="aenderung-verwerfen" disabled={busy} onClick={() => void verwerfen()}>
          <Icon name="undo" className="text-[16px]" /> {busy ? "Wird verworfen …" : "Änderung verwerfen"}
        </Button>
      ) : (
        <button
          type="button"
          data-testid="aenderung-verwerfen"
          disabled={busy}
          onClick={() => void verwerfen()}
          className="inline-flex items-center gap-1.5 font-label-md text-[12px] font-semibold underline-offset-2 hover:underline disabled:opacity-50"
        >
          <Icon name="undo" className="text-[15px]" /> {busy ? "Wird verworfen …" : "Änderung verwerfen"}
      </button>
      )}
      {fehler && <span className="mt-1 block text-error" role="alert">{fehler}</span>}
      {dialog}
    </>
  );
}

/** Antwortform von `GET /api/events/:id/kue-differenz` (KÜ V2, S3). Alle Beträge vom Server. */
export interface KueDifferenzDaten {
  alt: { nummer: string; signedAt: string | null; cents: number | null };
  neu: { quelle: "version" | "angebot"; nummer: string | null; cents: number } | null;
  differenzCents: number | null;
  bezahltCents: number;
  offenCents: number | null;
  ueberzahlungCents: number;
}

export const vorzeichen = (c: number) => `${c > 0 ? "+" : c < 0 ? "−" : "±"}${money(Math.abs(c))}`;

/**
 * KÜ V2, Stufe S3: „alt / neu / Differenz / bereits bezahlt“ — im Änderungs-Banner und in der
 * Preisvorschau des Angebotsversands. Rechnet nichts: jede Zahl kommt vom Server. Eine
 * Überzahlung (E10) wird mit der Abrechnung verrechnet; erstattet wird nur per Knopf in der Abrechnung.
 */
export function KueDifferenz({ eventId, optionId, kompakt = false }: { eventId: string; optionId?: string | null; kompakt?: boolean }) {
  const q = useApi<KueDifferenzDaten | null>(`/api/events/${eventId}/kue-differenz${optionId ? `?optionId=${optionId}` : ""}`);
  const d = q.data;
  if (!d || !d.neu) return null;
  const zeilen: { label: string; wert: string; testId: string; betont?: boolean }[] = [
    { label: `Gültig: ${d.alt.nummer}`, wert: d.alt.cents != null ? money(d.alt.cents) : "gemäß Bestellung", testId: "kue-diff-alt" },
    { label: d.neu.quelle === "version" && d.neu.nummer ? `Neu: ${d.neu.nummer}` : "Neu: geändertes Angebot", wert: money(d.neu.cents), testId: "kue-diff-neu" },
    ...(d.differenzCents != null ? [{ label: "Differenz", wert: vorzeichen(d.differenzCents), testId: "kue-diff-differenz", betont: true }] : []),
    { label: "Bereits bezahlt", wert: money(d.bezahltCents), testId: "kue-diff-bezahlt" },
  ];
  return (
    <div className={kompakt ? "mt-1.5" : "mt-3 rounded-lg bg-surface-container-low px-3 py-2 dark:bg-surface-variant/10"} data-testid="kue-differenz">
      <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 font-body-sm text-[11.5px]">
        {zeilen.map((z) => (
          <div key={z.testId} className="contents" data-testid={z.testId}>
            <dt className="min-w-0 truncate">{z.label}</dt>
            <dd className={`text-right tabular-nums ${z.betont ? "font-semibold" : ""}`}>{z.wert}</dd>
          </div>
        ))}
      </dl>
      {d.ueberzahlungCents > 0 ? (
        <p className="mt-1 font-body-sm text-[11.5px]" data-testid="kue-diff-ueberzahlung">
          Überzahlung {money(d.ueberzahlungCents)} nach der Unterschrift — wird mit der Abrechnung verrechnet. Sofort erstatten nur per Knopf in der Abrechnung (Zahlungen).
        </p>
      ) : d.offenCents != null && d.offenCents > 0 ? (
        <p className="mt-1 font-body-sm text-[11.5px]" data-testid="kue-diff-offen">
          Nach der Unterschrift offen: {money(d.offenCents)}. Zahlungslinks werden erst dann angepasst — bis dahin gilt {d.alt.nummer}.
        </p>
      ) : null}
    </div>
  );
}
